import React, { useState } from 'react';
import { Lock, Unlock, Pencil, CheckCircle, BarChart3, Shield, ChevronRight, Mail, Bell } from 'lucide-react';
import BrandName from './BrandName';
import PublicDesignerApplication from './PublicDesignerApplication';

const ComingSoon = ({ onAuthenticated }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showDesignerApplication, setShowDesignerApplication] = useState(false);
  
  // Newsletter subscription state
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState('idle'); // idle, loading, success, error
  const [subscribeMessage, setSubscribeMessage] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubscribeStatus('loading');
    setSubscribeMessage('');
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubscribeStatus('success');
        setSubscribeMessage(data.message || 'Successfully subscribed!');
        setEmail('');
      } else {
        setSubscribeStatus('error');
        setSubscribeMessage(data.detail || 'Failed to subscribe. Please try again.');
      }
    } catch (err) {
      setSubscribeStatus('error');
      setSubscribeMessage('Network error. Please try again later.');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Get password from environment variable
    const sitePassword = process.env.REACT_APP_SITE_PASSWORD || 'Create543687123!32';

    // Check password
    if (password === sitePassword) {
      // Store authentication in localStorage
      localStorage.setItem('site_authenticated', 'true');
      setTimeout(() => {
        onAuthenticated();
      }, 500);
    } else {
      setError('Incorrect password. Please try again.');
      setIsLoading(false);
      setPassword('');
    }
  };

  // Show designer application if user clicked the button
  if (showDesignerApplication) {
    return <PublicDesignerApplication onBack={() => setShowDesignerApplication(false)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Logo and Brand */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <img 
              src="/logo-nav.png" 
              alt="Create 3D Works Logo" 
              className="h-16 sm:h-20 md:h-24 w-auto object-contain max-w-full"
              style={{ maxHeight: '6rem' }}
            />
          </div>
          <p className="text-slate-700 text-lg sm:text-xl font-medium">Professional 3D Printing Services</p>
        </div>

        {/* Coming Soon Card */}
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl border border-slate-200">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-6">
              <Lock className="w-10 h-10 text-[#0007b5]" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Coming Soon
            </h2>
            <p className="text-slate-700 text-lg mb-2">
              We're preparing something amazing!
            </p>
            <p className="text-slate-600 text-base">
              Our new website is under construction and will be launching soon.
            </p>
          </div>

          {/* Newsletter Subscription */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#0007b5] rounded-full flex items-center justify-center">
                <Bell className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Get Notified</h3>
                <p className="text-sm text-slate-600">Be the first to know</p>
              </div>
            </div>
            
            {subscribeStatus === 'success' ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <p className="text-green-700 text-sm">{subscribeMessage}</p>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="space-y-3">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="w-full pl-10 pr-4 py-3 rounded-lg bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0007b5] focus:border-transparent"
                      disabled={subscribeStatus === 'loading'}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={subscribeStatus === 'loading' || !email}
                    className="px-6 py-3 bg-[#0007b5] hover:bg-[#000690] disabled:bg-slate-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors duration-200 flex items-center gap-2"
                  >
                    {subscribeStatus === 'loading' ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : (
                      'Subscribe'
                    )}
                  </button>
                </div>
                {subscribeStatus === 'error' && (
                  <p className="text-red-600 text-sm">{subscribeMessage}</p>
                )}
                <p className="text-sm text-slate-500">We'll never share your email. Unsubscribe anytime.</p>
              </form>
            )}
          </div>

          {/* Password Access */}
          <div className="mt-10 pt-8 border-t border-slate-200">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Unlock className="w-5 h-5 text-[#0007b5]" />
              <p className="text-slate-700 text-sm font-medium">Have access? Enter password to preview</p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0007b5] focus:border-transparent"
                  disabled={isLoading}
                />
              </div>
              
              {error && (
                <div className="bg-red-50 border border-red-300 rounded-lg p-3">
                  <p className="text-red-700 text-sm text-center">{error}</p>
                </div>
              )}
              
              <button
                type="submit"
                disabled={isLoading || !password}
                className="w-full bg-[#0007b5] hover:bg-[#000690] disabled:bg-slate-400 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Verifying...</span>
                  </>
                ) : (
                  <>
                    <Unlock className="w-5 h-5" />
                    <span>Access Site</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="mt-8 pt-6 border-t border-slate-200 text-center">
            <p className="text-slate-600 text-sm mb-2">Need access or have questions?</p>
            <a 
              href="mailto:hello@create3dworks.com" 
              className="text-[#0007b5] hover:text-[#000690] font-medium transition-colors"
            >
              hello@create3dworks.com
            </a>
          </div>
        </div>

        {/* Professional Designer Invitation Card */}
        <div className="mt-8 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-2xl p-6 border border-blue-200">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-blue-500/10 rounded-full flex items-center justify-center">
                <Pencil className="w-6 h-6 text-[#0007b5]" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Professional 3D Designers Welcome
              </h3>
              <p className="text-sm text-slate-700 mb-4">
                Want to learn more about joining our platform? Request more information about generating revenue from your designs while maintaining full IP.
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="inline-flex items-center px-3.5 py-1.5 rounded-full text-sm font-medium bg-green-50 text-green-700 border border-green-200">
                  <CheckCircle className="w-4 h-4 mr-1.5" />
                  Passive Income
                </span>
                <span className="inline-flex items-center px-3.5 py-1.5 rounded-full text-sm font-medium bg-green-50 text-green-700 border border-green-200">
                  <BarChart3 className="w-4 h-4 mr-1.5" />
                  Greater Reach
                </span>
                <span className="inline-flex items-center px-3.5 py-1.5 rounded-full text-sm font-medium bg-green-50 text-green-700 border border-green-200">
                  <Shield className="w-4 h-4 mr-1.5" />
                  IP Protected
                </span>
              </div>
              <button
                onClick={() => setShowDesignerApplication(true)}
                className="inline-flex items-center px-4 py-2 bg-[#0007b5] hover:bg-[#000690] text-white text-sm font-medium rounded-lg transition-colors"
              >
                Request Information
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} CREATE 3D Works. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ComingSoon;
